// Extraído e adaptado de "Lacan em 20 Frases — A Linguagem do Inconsciente" — William Marcos da Silva

export const CONCEITOS_LACAN = [
  {
    titulo: "O Inconsciente Estruturado como Linguagem",
    definicao:
      "Para Lacan, o inconsciente não é um depósito confuso de impulsos, mas um sistema organizado como uma língua: feito de significantes que se encadeiam, se deslocam e se substituem segundo uma lógica própria.",
    exemplo:
      "Um erro de digitação que troca o nome de uma pessoa por outro nome, repetido sempre na mesma situação, não é mero descuido — costuma seguir uma lógica de associação que vale a pena escutar.",
    casoResumo:
      "Um executivo que trocava, em e-mails, o nome do sócio pelo nome do irmão mais velho descobre, ao longo da análise, que os dois vínculos guardavam a mesma marca de rivalidade.",
    casoCompleto: [
      "Rafael, 41 anos, gerente de uma empresa familiar, procura análise após ser chamado atenção repetidas vezes por um mesmo tipo de erro: em e-mails formais dirigidos ao sócio da empresa, ele escrevia, no lugar do nome correto, o nome do irmão mais velho.",
      "A princípio, Rafael tratava isso como falta de atenção e cansaço. O analista, no entanto, não tomou o erro como acidente a ser corrigido, mas como uma pista a ser seguida: por que justamente aquele nome, e não outro qualquer, vinha ocupar o lugar do nome do sócio?",
      "Ao longo das sessões, Rafael foi reconstruindo uma história de disputa antiga com o irmão mais velho pela atenção do pai, e percebeu que o sócio ocupava, na dinâmica atual da empresa, um lugar de autoridade muito parecido com aquele que o irmão ocupara na infância.",
      "O caso ilustra bem a tese lacaniana: o inconsciente não guarda apenas lembranças, mas organiza a fala segundo uma cadeia de significantes que insiste em se repetir até ser escutada.",
    ],
    video: { url: null },
    quiz: {
      pergunta:
        "Segundo Lacan, o que a frase 'o inconsciente é estruturado como uma linguagem' propõe sobre lapsos e trocas de palavras?",
      opcoes: [
        "Que são apenas falhas de atenção sem nenhum sentido a decifrar",
        "Que obedecem a uma lógica de encadeamento de significantes que pode ser escutada",
        "Que só ocorrem em pessoas com transtornos de memória",
      ],
      correta: 1,
    },
    referenciaLivro: {
      titulo: "Lacan em 20 Frases — A Linguagem do Inconsciente",
      autor: "William Marcos da Silva",
    },
  },
  {
    titulo: "O Desejo do Outro",
    definicao:
      "Lacan afirma que o desejo nunca é puramente individual: desejamos em referência ao que supomos ser o desejo de alguém, buscando reconhecimento, aprovação ou lugar nos olhos do Outro.",
    exemplo:
      "Escolher uma profissão 'porque é isso que a família sempre esperou' é um exemplo cotidiano de como o próprio querer pode estar moldado pelo desejo suposto de outra pessoa.",
    casoResumo:
      "Uma jovem angustiada com a escolha de carreira percebe, em análise, que sua indecisão não era sobre o que gostava, mas sobre qual caminho agradaria aos pais.",
    casoCompleto: [
      "Beatriz, 22 anos, chega à análise em crise diante da escolha de curso universitário. Ela relata listas intermináveis de prós e contras, sem nunca conseguir se decidir, e descreve a situação como 'não saber o que realmente quero'.",
      "Nas sessões seguintes, ao invés de tentar descobrir uma 'vocação verdadeira' escondida, o analista escuta com atenção de quem vêm as frases que Beatriz repete sobre cada opção: 'meu pai ficaria orgulhoso', 'minha mãe sempre quis isso para mim', 'seria decepção para a família'.",
      "Aos poucos, Beatriz percebe que sua angústia não vinha da falta de desejo, mas do fato de que seu desejo estava emaranhado com o que ela supunha ser o desejo dos pais — e que nunca havia se perguntado, separadamente, o que ela mesma valorizava naquilo tudo.",
      "O caso mostra como o desejo, para Lacan, se constitui sempre em relação ao Outro: não se trata de eliminar essa influência, mas de reconhecê-la para que o sujeito possa se posicionar diante dela.",
    ],
    video: { url: null },
    quiz: {
      pergunta:
        "O que Lacan quer dizer com a frase 'o desejo é o desejo do Outro'?",
      opcoes: [
        "Que o desejo humano é sempre mediado pela alteridade, pelo olhar e pela suposta expectativa de outra pessoa",
        "Que só existe desejo verdadeiro quando ele é completamente independente dos outros",
        "Que desejar é sempre um ato egoísta e isolado",
      ],
      correta: 0,
    },
    referenciaLivro: {
      titulo: "Lacan em 20 Frases — A Linguagem do Inconsciente",
      autor: "William Marcos da Silva",
    },
  },
  {
    titulo: "O Inconsciente como Discurso do Outro",
    definicao:
      "Lacan sustenta que o sujeito não fabrica seu inconsciente sozinho: ele o recebe, em boa parte, através dos significantes transmitidos pela família, pela cultura e pela linguagem que o precede.",
    exemplo:
      "Uma pessoa que repete, sem perceber, frases como 'não posso falhar' ou 'preciso ser forte' está muitas vezes reproduzindo um discurso familiar que se tornou lei interna, e não uma convicção própria e original.",
    casoResumo:
      "Um paciente que se cobrava perfeição constante identifica, em análise, que a frase que repetia para si mesmo era, palavra por palavra, uma frase do pai.",
    casoCompleto: [
      "Diego, 35 anos, busca análise por exaustão: sente que não consegue relaxar nunca, pois qualquer deslize no trabalho lhe provoca uma culpa desproporcional. Ele descreve uma voz interna que repete, seca, 'erro não se permite'.",
      "Ao explorar a origem dessa frase, Diego percebe que não sabe dizer quando começou a pensar assim — ela sempre esteve ali, como uma regra óbvia. O analista propõe que ele investigue de onde vem, literalmente, esse enunciado.",
      "Em uma sessão, Diego se lembra, com surpresa, que essa era exatamente a frase que seu pai, um homem rígido e exigente, costumava dizer à mesa de jantar sempre que algum dos filhos cometia um erro na escola.",
      "O caso ilustra a tese de que o inconsciente fala com vozes que vêm de fora do sujeito: leis, frases e cobranças do Outro que se inscrevem como se fossem pensamento próprio, até que a análise permita distingui-las.",
    ],
    video: { url: null },
    quiz: {
      pergunta:
        "Segundo essa tese lacaniana, de onde vêm muitos dos conteúdos que organizam o inconsciente de um sujeito?",
      opcoes: [
        "Exclusivamente de experiências biológicas inatas",
        "De significantes transmitidos pela família, pela cultura e pela linguagem do Outro",
        "De escolhas totalmente conscientes e deliberadas",
      ],
      correta: 1,
    },
    referenciaLivro: {
      titulo: "Lacan em 20 Frases — A Linguagem do Inconsciente",
      autor: "William Marcos da Silva",
    },
  },
  {
    titulo: "Não Há Relação Sexual",
    definicao:
      "Com essa fórmula, Lacan não nega o sexo ou o amor, mas afirma que não existe uma fórmula simbólica que garanta o encaixe perfeito entre dois sujeitos: todo encontro é marcado por mal-entendidos e diferenças na forma de cada um gozar e desejar.",
    exemplo:
      "A frustração de quem acumula relacionamentos frustrados esperando 'a pessoa certa' costuma ignorar que nenhum encontro é uma engrenagem perfeita — todo casal precisa inventar, à sua maneira, um jeito de lidar com as diferenças.",
    casoResumo:
      "Um paciente que buscava 'a combinação perfeita' em aplicativos de relacionamento percebe, em análise, que o problema não era a escolha errada, mas a expectativa de uma harmonia que não existe.",
    casoCompleto: [
      "Thiago, 29 anos, chega à análise queixando-se de uma sequência de relacionamentos curtos, sempre interrompidos pela mesma sensação: 'não era bem aquilo que eu esperava'. Ele descreve os aplicativos de namoro como uma busca quase científica pela pessoa ideal.",
      "O analista escuta, ao longo de várias sessões, o padrão que se repete: por trás da frustração de cada novo relacionamento, Thiago carregava a fantasia de que, em algum lugar, existiria uma pessoa que se encaixaria nele sem atrito nenhum.",
      "Aos poucos, ao revisitar suas relações mais significativas, Thiago começa a perceber que os momentos de maior intimidade não vieram da ausência de diferenças, mas da capacidade dele e do parceiro de negociar, conversar e sustentar essas diferenças em vez de eliminá-las.",
      "O caso ilustra a ideia de que o amor não se sustenta por uma complementaridade natural entre duas pessoas, mas por uma construção sempre singular e nunca definitivamente resolvida.",
    ],
    video: { url: null },
    quiz: {
      pergunta:
        "O que Lacan quer dizer com a expressão 'não há relação sexual'?",
      opcoes: [
        "Que a prática sexual e o amor não existem de fato",
        "Que não existe uma fórmula simbólica de complementaridade perfeita entre os sexos, sendo o encontro sempre marcado por desencontros",
        "Que apenas alguns casais conseguem viver relações verdadeiras",
      ],
      correta: 1,
    },
    referenciaLivro: {
      titulo: "Lacan em 20 Frases — A Linguagem do Inconsciente",
      autor: "William Marcos da Silva",
    },
  },
  {
    titulo: "O Grande Outro Não Existe",
    definicao:
      "Lacan sustenta que não existe uma instância absoluta e completa de garantia do sentido — nem autoridade, ciência, religião ou algoritmo pode oferecer uma resposta final e infalível para o enigma de cada sujeito.",
    exemplo:
      "A busca incessante por um especialista, guru ou fórmula pronta que resolva todas as dúvidas da vida costuma esbarrar sempre no mesmo limite: nenhuma autoridade externa consegue eliminar a incerteza definitivamente.",
    casoResumo:
      "Uma paciente que seguia religiosamente conselhos de diferentes 'gurus' de autoajuda percebe, em análise, que a decepção repetida com cada um deles apontava para algo mais estrutural do que falta de sorte.",
    casoCompleto: [
      "Camila, 38 anos, relata em análise uma longa trajetória de busca por respostas prontas: cursos, livros de desenvolvimento pessoal, terapeutas alternativos e mentores diversos, sempre com a esperança de finalmente encontrar quem lhe desse a fórmula certa para viver.",
      "Cada nova fonte de autoridade trazia alívio temporário, seguido de uma decepção parecida: a resposta nunca era completa, sempre faltava algo, sempre surgia uma nova dúvida logo depois.",
      "Na análise, o trabalho não foi indicar mais uma autoridade confiável, mas ajudar Camila a perceber esse padrão: a expectativa de que existiria, em algum lugar, um saber total e definitivo sobre sua vida — e o efeito de vazio que cada tentativa de preencher essa expectativa deixava.",
      "O caso mostra como reconhecer os limites de qualquer autoridade externa, por mais qualificada que seja, pode abrir espaço para que o sujeito assuma suas próprias respostas, ainda que parciais e provisórias.",
    ],
    video: { url: null },
    quiz: {
      pergunta:
        "O que a tese 'o Grande Outro não existe' aponta sobre autoridades e saberes absolutos?",
      opcoes: [
        "Que toda autoridade externa é, em alguma medida, falha e incompleta",
        "Que a ciência e a religião sempre têm razão sobre tudo",
        "Que não se deve confiar em nenhum profissional",
      ],
      correta: 0,
    },
    referenciaLivro: {
      titulo: "Lacan em 20 Frases — A Linguagem do Inconsciente",
      autor: "William Marcos da Silva",
    },
  },
  {
    titulo: "A Verdade com Estrutura de Ficção",
    definicao:
      "Para Lacan, a verdade de um sujeito nunca se apresenta de forma direta e crua: ela só pode ser dita por meio de narrativas, cenas e histórias que o próprio sujeito constrói para dar sentido à sua experiência.",
    exemplo:
      "Ao contar repetidamente a mesma história sobre um episódio de vida, cada vez com pequenos detalhes diferentes, uma pessoa não está mentindo — está tentando encontrar, por tentativas narrativas, uma forma de dizer algo que resiste a ser dito diretamente.",
    casoResumo:
      "Um paciente reconta um mesmo episódio de infância de formas ligeiramente diferentes a cada sessão; é justamente nessas variações que a análise encontra pistas sobre sua verdade subjetiva.",
    casoCompleto: [
      "Marcelo, 46 anos, traz repetidamente à análise a lembrança de um dia em que se perdeu dos pais numa feira quando criança. A cada vez que reconta o episódio, algum detalhe muda: às vezes é a mãe quem o segura pela mão em seguida, às vezes é o pai; às vezes ele chora, às vezes descreve calma.",
      "Em vez de tentar estabelecer 'a versão verdadeira e exata' dos fatos, o analista escuta as variações como parte do material analítico: o que cada versão da história revela sobre o que Marcelo precisa dizer, e não apenas sobre o que aconteceu.",
      "Com o tempo, Marcelo percebe que a cena se tornou, ao longo dos anos, uma espécie de ficção pessoal através da qual ele processa um sentimento mais amplo de desamparo que atravessou parte de sua infância — algo que nunca poderia ser dito diretamente, apenas contornado por meio da história.",
      "O caso ilustra que, para a psicanálise, a verdade não é um fato a ser recuperado intacto, mas algo que se constrói e se revela através da forma como o sujeito narra sua própria experiência.",
    ],
    video: { url: null },
    quiz: {
      pergunta:
        "Segundo Lacan, como a verdade subjetiva costuma se apresentar na fala de um analisando?",
      opcoes: [
        "De forma direta, objetiva e sempre idêntica a cada relato",
        "Apenas através de exames médicos e testes padronizados",
        "Sob a forma de narrativas e ficções que o sujeito constrói para dar sentido à experiência",
      ],
      correta: 2,
    },
    referenciaLivro: {
      titulo: "Lacan em 20 Frases — A Linguagem do Inconsciente",
      autor: "William Marcos da Silva",
    },
  },
  {
    titulo: "O Sujeito como Efeito da Linguagem",
    definicao:
      "Lacan propõe que o sujeito não é uma essência ou substância pronta, mas algo que se constitui a partir da linguagem: somos nomeados e falados antes mesmo de conseguirmos falar por conta própria.",
    exemplo:
      "O nome que uma criança recebe, junto com as expectativas e histórias familiares associadas a ele, molda parte de quem ela se torna — muito antes de ela poder escolher ou compreender isso.",
    casoResumo:
      "Uma paciente que carregava o nome de uma tia falecida percebe, em análise, que boa parte de sua identidade fora moldada por expectativas ligadas a essa história familiar, e não por uma escolha própria.",
    casoCompleto: [
      "Larissa, 27 anos, comenta em análise, quase de passagem, que recebeu o nome de uma tia que morreu jovem, antes de seu nascimento. O analista nota o comentário e pergunta com mais atenção sobre isso nas sessões seguintes.",
      "Larissa relata que, ao longo da vida, ouviu diversas vezes frases como 'você tem o mesmo jeito dela' ou 'é impressionante como você parece com ela'. Sem perceber, ela havia incorporado traços e expectativas de uma pessoa que nunca conheceu, apenas por carregar seu nome.",
      "Na análise, Larissa começa a diferenciar o que era, de fato, seu, do que havia sido depositado nela pela história familiar associada ao nome — um processo de separar-se, aos poucos, de um roteiro que não escolhera.",
      "O caso mostra como o sujeito, para Lacan, se constitui na e pela linguagem: nomes, frases e histórias que o precedem organizam boa parte daquilo que, mais tarde, ele viverá como sendo 'sua própria identidade'.",
    ],
    video: { url: null },
    quiz: {
      pergunta:
        "O que significa dizer que, para Lacan, o sujeito é 'efeito da linguagem'?",
      opcoes: [
        "Que a personalidade é determinada apenas pela genética",
        "Que o sujeito se constitui na relação com a linguagem que o precede, e não como uma essência pronta antes dela",
        "Que só existe sujeito depois que a pessoa aprende a escrever",
      ],
      correta: 1,
    },
    referenciaLivro: {
      titulo: "Lacan em 20 Frases — A Linguagem do Inconsciente",
      autor: "William Marcos da Silva",
    },
  },
  {
    titulo: "A Fantasia como Moldura do Desejo",
    definicao:
      "Lacan entende a fantasia não como uma ilusão inútil, mas como o cenário interno que permite ao sujeito sustentar e dar forma ao seu desejo, mesmo que este permaneça, em essência, enigmático.",
    exemplo:
      "Uma pessoa que idealiza repetidamente o mesmo tipo de emprego 'perfeito', mesmo trocando de área, pode estar sustentada por um mesmo roteiro fantasístico de reconhecimento, mais do que pela função em si.",
    casoResumo:
      "Um paciente que trocava de emprego a cada poucos meses, sempre em busca do 'trabalho ideal', descobre em análise que perseguia, na verdade, um mesmo cenário fantasístico de reconhecimento.",
    casoCompleto: [
      "Eduardo, 33 anos, chega à análise após pedir demissão pela quinta vez em três anos, sempre com a justificativa de que o trabalho anterior 'não era bem aquilo'. Ele descreve cada nova vaga com entusiasmo, imaginando cenas de reconhecimento público por seu talento.",
      "O analista percebe que, independentemente da função ou da empresa, a fantasia de Eduardo se repete quase palavra por palavra: um momento em que colegas e chefes o aplaudem por uma ideia brilhante que só ele teria tido.",
      "Ao explorar a origem dessa cena, Eduardo se lembra de situações de infância em que se sentia invisível diante de um irmão mais talentoso academicamente, e de como fantasiava, então, cenas semelhantes de reconhecimento diante da família.",
      "O caso ilustra como a fantasia funciona como moldura do desejo: não é o emprego em si que Eduardo buscava, mas a repetição de um cenário que lhe permitia sustentar, de forma protegida, seu desejo de ser reconhecido.",
    ],
    video: { url: null },
    quiz: {
      pergunta:
        "Qual é a função da fantasia na teoria lacaniana do desejo?",
      opcoes: [
        "Ela é apenas uma distração sem relação com o desejo real do sujeito",
        "Ela fornece um cenário que sustenta e dá forma ao desejo do sujeito",
        "Ela deve ser eliminada por completo para que o sujeito seja saudável",
      ],
      correta: 1,
    },
    referenciaLivro: {
      titulo: "Lacan em 20 Frases — A Linguagem do Inconsciente",
      autor: "William Marcos da Silva",
    },
  },
  {
    titulo: "Nome-do-Pai e Função Paterna",
    definicao:
      "Mais do que a figura biológica do pai, o Nome-do-Pai é, para Lacan, um significante que introduz a lei e o limite na relação entre a criança e a mãe, permitindo que o desejo se organize para além dessa fusão inicial.",
    exemplo:
      "Uma figura de autoridade que estabelece regras claras e consistentes — seja um pai, uma mãe ou outro adulto de referência — pode cumprir essa função simbólica de introduzir limite, independentemente do arranjo familiar.",
    casoResumo:
      "Uma mãe solo relata preocupação por criar o filho sozinha; a análise revela que o que faltava não era uma figura paterna biológica, mas alguém que sustentasse a função de lei e limite.",
    casoCompleto: [
      "Fernanda, 34 anos, mãe solo de um menino de 6 anos, procura análise preocupada com o desenvolvimento do filho, temendo que a ausência do pai biológico, que não participa da criação, deixasse uma lacuna irreparável.",
      "No trabalho analítico, discute-se que a questão central não é a presença física de um homem específico, mas se existe, na vida da criança, alguém que sustente consistentemente limites, regras e a ideia de que nem tudo é permitido — a chamada função paterna.",
      "Fernanda percebe que ela mesma, junto com o avô materno, vinha exercendo essa função de forma consistente havia anos, ainda que sem perceber que aquilo tinha um nome e um papel estruturante na vida do filho.",
      "O caso ilustra que o Nome-do-Pai, para Lacan, é uma função simbólica de lei e limite, que pode ser exercida por diferentes figuras, e não uma exigência de um modelo familiar específico.",
    ],
    video: { url: null },
    quiz: {
      pergunta:
        "O que caracteriza o 'Nome-do-Pai' na teoria lacaniana?",
      opcoes: [
        "É necessariamente a presença física e biológica do pai",
        "É uma função simbólica de lei e limite que organiza o desejo, podendo ser exercida por diferentes figuras",
        "É um conceito exclusivamente ligado à religião",
      ],
      correta: 1,
    },
    referenciaLivro: {
      titulo: "Lacan em 20 Frases — A Linguagem do Inconsciente",
      autor: "William Marcos da Silva",
    },
  },
  {
    titulo: "O Estádio do Espelho",
    definicao:
      "Lacan descreve como, ainda bebê, o sujeito se identifica com sua imagem refletida — no espelho ou no olhar do Outro — formando um 'eu' unificado que, na verdade, é uma construção idealizada e alienante.",
    exemplo:
      "A diferença entre a imagem cuidadosamente editada que alguém posta nas redes sociais e a experiência real e fragmentada do dia a dia repete, em outra escala, essa alienação entre o eu idealizado e a vivência concreta.",
    casoResumo:
      "Uma jovem que vive angustiada pela comparação com sua própria imagem nas redes sociais percebe, em análise, que a distância entre o 'eu' idealizado e sua experiência real sempre existiu, apenas ganhou nova forma.",
    casoCompleto: [
      "Juliana, 24 anos, procura análise relatando ansiedade intensa antes de postar fotos em redes sociais, e uma sensação de fracasso pessoal quando a imagem publicada não corresponde ao que ela sente por dentro.",
      "Ao longo do trabalho analítico, Juliana percebe que essa distância entre uma imagem idealizada de si e a experiência interna, muitas vezes confusa e fragmentada, não é uma novidade das redes sociais — ela relata memórias de infância em que já se comparava a uma versão 'perfeita' de si mesma refletida no espelho do quarto.",
      "O analista aponta que essa tensão entre a imagem unificada que reconhecemos como 'eu' e a experiência real, sempre mais dispersa, é estruturante — não um problema a ser eliminado, mas algo a ser reconhecido e relativizado.",
      "O caso ilustra como o estádio do espelho descrito por Lacan continua operando ao longo da vida: a identidade se apoia numa imagem idealizada, e parte do trabalho analítico é ajudar o sujeito a não se cobrar por não corresponder integralmente a ela.",
    ],
    video: { url: null },
    quiz: {
      pergunta:
        "O que o 'estádio do espelho' descreve na constituição do eu, segundo Lacan?",
      opcoes: [
        "Um processo puramente biológico sem relação com a imagem",
        "A identificação da criança com uma imagem unificada de si, que é, na verdade, idealizada e alienante",
        "Um estágio que ocorre apenas na adolescência",
      ],
      correta: 1,
    },
    referenciaLivro: {
      titulo: "Lacan em 20 Frases — A Linguagem do Inconsciente",
      autor: "William Marcos da Silva",
    },
  },
  {
    titulo: "A Cadeia Significante",
    definicao:
      "Para Lacan, cada significante ganha sentido não por si só, mas por sua posição em relação a outros significantes — formando uma cadeia em constante deslizamento que estrutura a fala e o inconsciente.",
    exemplo:
      "Numa sessão de terapia, um paciente pode começar falando do trabalho, deslizar para o pai, depois para uma discussão recente com a esposa — associações que, à primeira vista parecem soltas, mas seguem um fio de sentido a ser escutado.",
    casoResumo:
      "Um paciente que parecia 'pular de assunto em assunto' nas sessões revela, quando o analista segue o fio de suas associações, uma lógica interna precisa ligando os temas.",
    casoCompleto: [
      "Vinícius, 40 anos, é descrito por ele mesmo como alguém que 'não consegue manter o fio da meada' quando fala em análise: começa comentando um problema no trabalho, passa a falar do carro que quebrou, e termina relatando uma discussão com o irmão.",
      "Em vez de tentar organizar o discurso de Vinícius de forma linear, o analista passa a prestar atenção justamente nessas passagens, anotando mentalmente a sequência dos temas que se sucediam nas sessões.",
      "Depois de algumas semanas, um padrão se torna visível: toda vez que Vinícius fala de perder o controle sobre algo — o trabalho, o carro, a relação com o irmão — ele está, na verdade, contornando uma cena antiga em que se sentiu impotente diante do pai.",
      "O caso mostra como a cadeia significante opera: os temas que parecem desconexos na fala espontânea seguem, na verdade, uma lógica de deslocamento que a escuta atenta pode revelar.",
    ],
    video: { url: null },
    quiz: {
      pergunta:
        "Como o significante se define dentro da cadeia significante, segundo Lacan?",
      opcoes: [
        "Por seu conteúdo fixo e isolado",
        "Por sua posição em relação aos outros significantes na cadeia",
        "Apenas pelo som que produz, sem qualquer relação com sentido",
      ],
      correta: 1,
    },
    referenciaLivro: {
      titulo: "Lacan em 20 Frases — A Linguagem do Inconsciente",
      autor: "William Marcos da Silva",
    },
  },
  {
    titulo: "A Ética do Desejo",
    definicao:
      "Lacan propõe uma ética distinta da moral tradicional: não se trata de adaptar o sujeito às normas sociais, mas de que ele não recue diante do próprio desejo, assumindo a responsabilidade por sua verdade singular.",
    exemplo:
      "Uma pessoa que abandona repetidamente projetos importantes por medo do julgamento alheio pode estar, sem perceber, cedendo diante do próprio desejo em nome de uma acomodação mais confortável.",
    casoResumo:
      "Uma paciente que desistia de projetos pessoais sempre no momento decisivo percebe, em análise, que o padrão se repetia como uma forma de evitar assumir o próprio desejo.",
    casoCompleto: [
      "Patrícia, 37 anos, relata em análise uma sequência de projetos abandonados: um curso de especialização, um plano de mudança de cidade, um pedido de promoção — todos interrompidos pouco antes de se concretizarem, sempre com uma justificativa razoável.",
      "O analista nota que essas desistências, embora racionalizadas de formas diferentes a cada vez, ocorrem sempre no mesmo ponto do processo: exatamente quando o projeto começa a exigir de Patrícia um compromisso público com aquilo que ela deseja.",
      "Ao investigar esse padrão, Patrícia percebe que cada desistência a protegia de uma exposição temida — o julgamento de familiares que sempre valorizaram escolhas mais seguras e previsíveis do que as suas.",
      "O caso ilustra a ética lacaniana do desejo: o convite não é a realizar todo impulso, mas a não recuar sistematicamente diante daquilo que constitui a verdade singular de cada sujeito, mesmo quando isso implica enfrentar desconforto.",
    ],
    video: { url: null },
    quiz: {
      pergunta:
        "Qual é o núcleo da ética da psicanálise segundo Lacan?",
      opcoes: [
        "Adaptar-se completamente às expectativas sociais",
        "Satisfazer todo impulso sem qualquer limite",
        "Não ceder de seu desejo, assumindo a verdade singular do próprio sujeito",
      ],
      correta: 2,
    },
    referenciaLivro: {
      titulo: "Lacan em 20 Frases — A Linguagem do Inconsciente",
      autor: "William Marcos da Silva",
    },
  },
  {
    titulo: "O Gozo e a Lei",
    definicao:
      "Lacan vai além do princípio do prazer freudiano ao propor o conceito de gozo: uma experiência que envolve excesso, sofrimento e até transgressão, e que só pode existir em relação a um limite imposto pela lei.",
    exemplo:
      "Comportamentos repetitivos que trazem sofrimento, como compulsões ou escolhas amorosas destrutivas, muitas vezes não visam apenas o prazer, mas revelam a insistência do sujeito em tocar algo desse gozo excessivo.",
    casoResumo:
      "Um paciente que se envolvia repetidamente em relacionamentos que o feriam, mesmo reconhecendo o padrão, começa a entender em análise que buscava ali algo além do simples prazer.",
    casoCompleto: [
      "Bruno, 30 anos, relata em análise um padrão que se repete há anos: relacionamentos intensos e desgastantes, marcados por ciúme, brigas e reconciliações dramáticas, que ele mesmo reconhece como prejudiciais, mas dos quais tem dificuldade de se afastar.",
      "Bruno descreve que sabe, racionalmente, que esses relacionamentos lhe fazem mal, e ainda assim sente uma atração quase incontrolável por reproduzir o mesmo tipo de dinâmica, mesmo depois de terminar um vínculo desses.",
      "O trabalho analítico não busca simplesmente convencê-lo a 'escolher melhor', mas explorar o que esse padrão repetido revela sobre uma satisfação que ultrapassa o simples prazer — uma espécie de excesso que insiste, mesmo contra os próprios interesses de Bruno.",
      "O caso ilustra o conceito lacaniano de gozo: uma dimensão que não se reduz à busca de bem-estar, e que a clínica ajuda o sujeito a reconhecer e situar, em vez de simplesmente reprimir ou negar.",
    ],
    video: { url: null },
    quiz: {
      pergunta:
        "O que distingue o conceito lacaniano de 'gozo' do simples prazer?",
      opcoes: [
        "O gozo é idêntico ao prazer, apenas com outro nome",
        "O gozo envolve uma dimensão de excesso e sofrimento que ultrapassa a busca por bem-estar",
        "O gozo só existe em contextos religiosos",
      ],
      correta: 1,
    },
    referenciaLivro: {
      titulo: "Lacan em 20 Frases — A Linguagem do Inconsciente",
      autor: "William Marcos da Silva",
    },
  },
  {
    titulo: "O Objeto a",
    definicao:
      "O objeto a é, para Lacan, aquilo que causa o desejo sem nunca se confundir com um objeto real: não é a coisa desejada em si, mas a falta em torno da qual o desejo se organiza.",
    exemplo:
      "A sensação de que 'está quase lá, falta só mais uma conquista' para se sentir realizado, mesmo depois de alcançar metas importantes, ilustra como o desejo persegue sempre algo que escapa, e não um objeto que o esgote.",
    casoResumo:
      "Um empresário bem-sucedido, insatisfeito apesar de cada nova conquista, percebe em análise que perseguia não os objetivos em si, mas algo que sempre escapava a eles.",
    casoCompleto: [
      "Sérgio, 50 anos, empresário de sucesso, procura análise intrigado com sua própria insatisfação crônica: a cada meta alcançada — um novo negócio, um novo carro, um reconhecimento público — a sensação de realização dura pouco, seguida rapidamente por um novo alvo a perseguir.",
      "O analista não tenta ajudar Sérgio a encontrar 'a meta certa que finalmente o satisfaça', mas explora com ele esse padrão de busca incessante, perguntando o que exatamente ele imagina que cada conquista deveria lhe trazer.",
      "Aos poucos, Sérgio percebe que não é o carro, o negócio ou o reconhecimento em si que ele busca, mas uma sensação de completude que nenhum objeto específico consegue de fato entregar — algo que sempre escorrega para além do objeto alcançado.",
      "O caso ilustra o conceito de objeto a: o desejo não se dirige a coisas que o esgotam, mas a uma falta estrutural que os objetos apenas encarnam parcialmente, sem nunca preenchê-la por completo.",
    ],
    video: { url: null },
    quiz: {
      pergunta:
        "O que é o 'objeto a' na teoria lacaniana?",
      opcoes: [
        "Um objeto material específico que, uma vez conquistado, satisfaz plenamente o desejo",
        "A causa do desejo, ligada a uma falta estrutural, e não um objeto real a ser possuído",
        "Um sinônimo lacaniano para o ego freudiano",
      ],
      correta: 1,
    },
    referenciaLivro: {
      titulo: "Lacan em 20 Frases — A Linguagem do Inconsciente",
      autor: "William Marcos da Silva",
    },
  },
  {
    titulo: "O Discurso do Analista",
    definicao:
      "Lacan descreve o discurso do analista como uma posição que subverte a lógica do saber e do poder: o analista não ocupa o lugar de quem detém respostas prontas, mas sustenta a escuta que permite ao sujeito produzir seu próprio saber.",
    exemplo:
      "Um bom analista, ao contrário do que muitos esperam, evita dar conselhos diretos — sua função é sustentar perguntas e silêncios que permitam ao paciente chegar às próprias descobertas.",
    casoResumo:
      "Um paciente insatisfeito por o analista 'nunca dar respostas diretas' percebe, com o tempo, que era justamente essa postura que o levava a encontrar suas próprias soluções.",
    casoCompleto: [
      "Henrique, 45 anos, nas primeiras semanas de análise, demonstra frustração recorrente: 'eu conto o problema e você não me diz o que fazer', reclama, comparando a experiência a consultas médicas em que sai com uma prescrição clara.",
      "O analista sustenta a posição de não fornecer soluções prontas, mas devolve as próprias perguntas de Henrique com outras perguntas, e aponta contradições e repetições na sua fala, sem nunca dizer 'o que ele deveria fazer'.",
      "Com o tempo, Henrique nota uma mudança: ao invés de esperar uma resposta externa, ele mesmo começa a formular, sessão após sessão, hipóteses sobre seus próprios impasses — e a testar pequenas mudanças em sua vida a partir dessas hipóteses.",
      "O caso ilustra o discurso do analista: uma posição ética que não confirma a expectativa de um saber pronto, mas sustenta um espaço em que o próprio sujeito é convocado a produzir seu saber.",
    ],
    video: { url: null },
    quiz: {
      pergunta:
        "Qual posição caracteriza o discurso do analista, segundo Lacan?",
      opcoes: [
        "A de mestre que fornece soluções e conselhos prontos",
        "A de quem sustenta a escuta para que o próprio sujeito produza seu saber, sem ocupar o lugar de detentor de respostas",
        "A de professor que ensina técnicas de comportamento",
      ],
      correta: 1,
    },
    referenciaLivro: {
      titulo: "Lacan em 20 Frases — A Linguagem do Inconsciente",
      autor: "William Marcos da Silva",
    },
  },
  {
    titulo: "O Sujeito Suposto Saber",
    definicao:
      "Na transferência, o analisando costuma supor que o analista detém um saber especial sobre ele — mesmo sem provas concretas disso. Lacan chama essa crença de sujeito suposto saber, motor fundamental do processo analítico.",
    exemplo:
      "A confiança inicial de que 'o terapeuta certamente sabe o que há de errado comigo' é o que costuma mover alguém a se abrir numa análise, mesmo antes de qualquer interpretação ter sido feita.",
    casoResumo:
      "Uma paciente que acreditava que o analista 'já sabia' o que estava por trás de seus sintomas percebe, ao longo do processo, que essa crença ia dando lugar à sua própria produção de sentido.",
    casoCompleto: [
      "Renata, 29 anos, inicia a análise convicta de que o analista, só de escutá-la algumas vezes, já teria percebido a raiz de sua ansiedade — e aguarda, session após sessão, que ele revele esse suposto diagnóstico oculto.",
      "O analista não confirma nem desmente essa expectativa diretamente; sustenta a escuta e devolve a Renata suas próprias palavras, suas contradições, os pontos em que ela mesma hesita ou se surpreende com o que diz.",
      "Ao longo de meses, Renata percebe uma mudança sutil: em vez de esperar passivamente por uma revelação do analista, ela mesma começa a formular e testar compreensões sobre sua própria ansiedade, cada vez mais autora de suas interpretações.",
      "O caso ilustra o sujeito suposto saber: a crença inicial de que o analista sabe é o que sustenta o engajamento na análise, mas o processo caminha para que o próprio sujeito assuma a autoria de seu saber.",
    ],
    video: { url: null },
    quiz: {
      pergunta:
        "O que é o 'sujeito suposto saber' na teoria lacaniana da transferência?",
      opcoes: [
        "Uma qualidade real e comprovada que todo analista possui",
        "A crença do analisando de que o analista detém um saber sobre ele, o que move a transferência",
        "Um teste de inteligência aplicado no início da análise",
      ],
      correta: 1,
    },
    referenciaLivro: {
      titulo: "Lacan em 20 Frases — A Linguagem do Inconsciente",
      autor: "William Marcos da Silva",
    },
  },
  {
    titulo: "A Lógica do Não-Todo Feminino",
    definicao:
      "Com a frase provocadora 'a Mulher não existe', Lacan não nega as mulheres reais, mas aponta que não há um significante universal capaz de representar toda a feminilidade — a posição feminina se estrutura como um 'não-todo', irredutível a uma essência única.",
    exemplo:
      "A dificuldade de definir 'o que toda mulher quer' ou 'como toda mulher é' com uma única fórmula reflete essa impossibilidade lógica de universalizar a experiência feminina.",
    casoResumo:
      "Uma paciente angustiada por não se reconhecer em nenhum modelo pronto de 'mulher ideal' encontra, em análise, alívio ao entender que essa não correspondência é estrutural, e não um defeito pessoal.",
    casoCompleto: [
      "Isabela, 31 anos, relata em análise um mal-estar antigo: sente que nunca se encaixou completamente em nenhuma das imagens de mulher que a cultura lhe ofereceu ao longo da vida, e vive isso como uma falha pessoal, algo que precisaria corrigir.",
      "Ao explorar essa questão, o analista não busca ajudar Isabela a se encaixar melhor em algum modelo, nem propor um modelo alternativo mais 'correto', mas questiona a própria ideia de que deveria existir um molde único ao qual toda mulher deveria corresponder.",
      "Isabela passa a perceber, ao longo das sessões, que sua dificuldade em se ver representada por completo em qualquer imagem pronta não era uma falha sua, mas algo estrutural: nenhuma fórmula geral consegue de fato abarcar toda a singularidade de uma experiência subjetiva.",
      "O caso ilustra a lógica lacaniana do não-todo: reconhecer que não existe um significante universal e definitivo para o feminino abre espaço para que cada mulher construa sua posição de forma singular, sem se medir por um padrão impossível.",
    ],
    video: { url: null },
    quiz: {
      pergunta:
        "O que a frase 'a Mulher não existe' propõe, segundo Lacan?",
      opcoes: [
        "Que mulheres reais não existem",
        "Que não há um significante universal capaz de representar toda a feminilidade, sendo essa posição um 'não-todo'",
        "Que todas as mulheres pensam e sentem exatamente da mesma forma",
      ],
      correta: 1,
    },
    referenciaLivro: {
      titulo: "Lacan em 20 Frases — A Linguagem do Inconsciente",
      autor: "William Marcos da Silva",
    },
  },
  {
    titulo: "O Real que Escapa à Palavra",
    definicao:
      "Lacan chama de real aquilo que resiste a ser inteiramente simbolizado ou colocado em palavras — um resto sempre presente, que a linguagem tenta, mas nunca consegue, capturar por completo.",
    exemplo:
      "Diante de uma perda muito grande, é comum que a pessoa sinta que 'nenhuma palavra dá conta' do que sente — não por falta de vocabulário, mas porque algo daquela experiência realmente escapa à fala.",
    casoResumo:
      "Um paciente que tentava, sem sucesso, 'explicar direito' o que sentiu ao presenciar um acidente percebe, em análise, que parte dessa experiência talvez nunca coubesse inteiramente em palavras.",
    casoCompleto: [
      "Gabriel, 39 anos, procura análise meses depois de presenciar um grave acidente de trânsito. Ele relata, frustrado, que já tentou contar o episódio de várias formas para amigos e familiares, mas sente que nunca consegue 'traduzir direito' o que viveu naquele instante.",
      "Nas sessões, o analista não insiste para que Gabriel encontre a descrição 'correta e completa' do evento, mas acompanha as tentativas sucessivas de Gabriel de se aproximar daquilo, através de palavras diferentes, silêncios, gestos e pausas.",
      "Aos poucos, Gabriel deixa de cobrar de si mesmo uma narrativa perfeita e passa a conviver de outra forma com a lembrança: não eliminando o impacto do evento, mas encontrando uma relação mais suportável com aquilo que, de fato, resiste a ser dito por completo.",
      "O caso ilustra a noção lacaniana de real: há sempre um resto de experiência que a linguagem não consegue capturar inteiramente, e parte do trabalho analítico é ajudar o sujeito a conviver com esse limite, em vez de insistir em superá-lo pela fala perfeita.",
    ],
    video: { url: null },
    quiz: {
      pergunta:
        "O que caracteriza o 'real' na teoria lacaniana?",
      opcoes: [
        "É tudo aquilo que pode ser perfeitamente descrito em palavras",
        "É aquilo que resiste à simbolização plena, um resto que a linguagem não consegue capturar por completo",
        "É sinônimo de realidade objetiva e mensurável",
      ],
      correta: 1,
    },
    referenciaLivro: {
      titulo: "Lacan em 20 Frases — A Linguagem do Inconsciente",
      autor: "William Marcos da Silva",
    },
  },
  {
    titulo: "O Sintoma como Solução Singular",
    definicao:
      "Nos últimos anos de seu ensino, Lacan passa a entender o sintoma não apenas como um problema a ser eliminado, mas como uma invenção singular que amarra o sujeito e lhe permite sustentar sua própria existência.",
    exemplo:
      "Um ritual pessoal aparentemente estranho, mas que ajuda alguém a atravessar momentos de angústia, pode ser menos um 'defeito a corrigir' e mais uma solução criativa e particular que a pessoa encontrou para lidar consigo mesma.",
    casoResumo:
      "Um paciente incomodado por um hábito compulsivo de organizar objetos antes de qualquer decisão importante descobre, em análise, que esse ritual funcionava como uma forma pessoal de conter uma angústia mais profunda.",
    casoCompleto: [
      "André, 42 anos, chega à análise incomodado com um hábito que considera irracional: antes de qualquer decisão importante — uma reunião, uma conversa difícil, uma viagem —, ele precisa organizar meticulosamente objetos de sua mesa, ou a ansiedade se torna insuportável.",
      "Inicialmente, André busca apenas 'se livrar' desse comportamento, que classifica como um defeito a ser eliminado o quanto antes. O analista, no entanto, propõe investigar com mais cuidado o que aconteceria se esse ritual simplesmente desaparecesse.",
      "Ao explorar essa questão, André percebe que o ritual, por mais estranho que pareça de fora, cumpre uma função precisa: ele lhe dá uma sensação momentânea de controle diante de situações em que se sente profundamente exposto e vulnerável.",
      "Em vez de simplesmente eliminar o comportamento, a análise ajuda André a compreender sua função e, aos poucos, a encontrar outras formas — mais flexíveis — de lidar com essa vulnerabilidade, sem descartar de forma abrupta algo que, à sua maneira, o vinha sustentando.",
    ],
    video: { url: null },
    quiz: {
      pergunta:
        "Como Lacan passa a entender o sintoma em seu último ensino?",
      opcoes: [
        "Como algo que deve ser eliminado o mais rápido possível, sem exceção",
        "Como uma invenção singular que amarra e sustenta a existência do sujeito, e não apenas um problema",
        "Como um sinal exclusivamente biológico, sem relação com a história do sujeito",
      ],
      correta: 1,
    },
    referenciaLivro: {
      titulo: "Lacan em 20 Frases — A Linguagem do Inconsciente",
      autor: "William Marcos da Silva",
    },
  },
];
